import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import Lenis from '@studio-freight/lenis';
import Marquee from 'react-fast-marquee';

// Assets
import gameController from '../assets/assets for home page/game-controller.png';
import mockup1 from '../assets/assets for home page/mockup1.png';
import community from '../assets/assets for home page/community.png';
import tournament from '../assets/assets for home page/tournament.png';
import gaming from '../assets/assets for home page/gaming.png';
import gift from '../assets/assets for home page/gift.png';
import pinkStars from '../assets/assets for home page/pink-stars.png';
import illustrationBtn from '../assets/assets for home page/illustration-2-btn.png';
import partner1 from '../assets/assets for home page/illustrator-partner-1.png';
import partner3 from '../assets/assets for home page/illustrator-partner-3.png';
import heroVideo from '../assets/vids/hero.mp4';
import slideVideo from '../assets/vids/slider.mp4';  
import dtLogo from '../assets/images/logo/logo_dark.png';
import { dtShopBrands } from '../assets/dt-data/data-dtshop';
import './home-new.css';

// ----- Reusable animation variants -----
const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    show: (i = 0) => ({
        opacity: 1, y: 0,
        transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 0.9, 0.3, 1] }
    })
};

const splitWord = (text) => text.split(' ');

function HomeNew() {
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: heroRef,
        offset: ['start start', 'end start']
    });

    // Parallax transforms for hero clouds
    const cloudLeftY = useTransform(scrollYProgress, [0, 1], [0, -150]);
    const cloudRightY = useTransform(scrollYProgress, [0, 1], [0, -250]);
    const heroTextY = useTransform(scrollYProgress, [0, 1], [0, 200]);
    const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

    // Lenis smooth scroll
    useEffect(() => {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduce) return;

        const lenis = new Lenis({
            duration: 1.15,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            smoothWheel: true
        });
        function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
        const rafId = requestAnimationFrame(raf);
        return () => {
            cancelAnimationFrame(rafId);
            lenis.destroy();
        };
    }, []);

    // Feature rows data
    const features = [
        {
            img: mockup1,
            title: 'Voucher Vault',
            body: 'Store all your gift cards & vouchers in one secure, cloud-synced vault. Access anytime, redeem anywhere.',
            side: 'right'
        },
        {
            img: community,
            title: 'Rewards Community',
            body: 'Join thousands of smart shoppers unlocking exclusive drops, cashback offers, and early access deals every day.',
            side: 'left'
        },
        {
            img: tournament,
            title: 'Earn on Every Spend',
            body: 'Turn shopping into earnings. Collect DT Points on every purchase and redeem them across 100+ premium brands.',
            side: 'right',
            extra: gaming
        },
        {
            img: gift,
            title: 'Passion That Pays',
            body: 'Refer friends, complete missions and level up your rewards tier. The more you engage, the more you earn.',
            side: 'left',
            isIcon: true
        }
    ];

    // Use top 16 brand names for partner marquee
    const partnerBrands = dtShopBrands.slice(0, 16);

    return (
        <div className="hn-page">

            {/* ===================== HERO ===================== */}
            <section className="hn-hero" ref={heroRef}>
                {/* Starry sky */}
                <div className="hn-stars" aria-hidden="true"></div>
                <div className="hn-stars hn-stars-2" aria-hidden="true"></div>

                {/* Decorative icons floating near headline */}
                <motion.img
                    src={gameController}
                    alt=""
                    aria-hidden="true"
                    className="hn-float-icon hn-float-controller"
                    animate={{ y: [0, -12, 0], rotate: [-8, 8, -8] }}
                    transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
                />
                <motion.div
                    className="hn-float-icon hn-float-gem"
                    animate={{ y: [0, -10, 0], rotate: [0, 15, 0] }}
                    transition={{ duration: 3.8, repeat: Infinity, ease: 'easeInOut' }}
                    aria-hidden="true"
                />

                {/* Headline: Engage Share Belong */}
                <motion.div className="hn-hero-content">
                    <h1 className="hn-hero-title">
                        <motion.span
                            className="hn-word hn-word-white"
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.1 }}
                        >
                            Vouchers
                        </motion.span>
                        <motion.span
                            className="hn-word hn-word-tape"
                            initial={{ opacity: 0, y: 50, rotate: -6 }}
                            animate={{ opacity: 1, y: 0, rotate: -1 }}
                            transition={{ duration: 0.7, delay: 0.25 }}
                        >
                            <span className="hn-tape-tl"></span>
                            <span className="hn-tape-tr"></span>
                            rewards
                        </motion.span>
                        <motion.span
                            className="hn-word hn-word-white"
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.4 }}
                        >
                            Vault
                        </motion.span>
                    </h1>
                </motion.div>

                {/* Hero Video */}
                <motion.div
                    className="hn-video-container"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                >
                    <video
                        className="hn-hero-video"
                        autoPlay
                        loop
                        muted
                        playsInline
                        src={heroVideo}
                    >
                        Your browser does not support the video tag.
                    </video>
                </motion.div>
            </section>

            {/* ===================== ABOUT ===================== */}
            <section className="hn-about">
                <motion.div
                    className="hn-about-inner"
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.4 }}
                >
                    <motion.h2 variants={fadeUp} className="hn-section-label">
                        About DT Wallet
                    </motion.h2>
                    <motion.p variants={fadeUp} custom={1} className="hn-about-text">
                        DT Wallet is the all-in-one voucher & rewards platform where you discover top brands,
                        save more on every purchase, and turn everyday spending into real earnings. Shop smart,
                        earn smarter.
                    </motion.p>
                </motion.div>
            </section>

            {/* ===================== WHY ===================== */}
            <section className="hn-why">
                {/* Background decorations */}
                <img src={partner1} alt="" aria-hidden="true" className="hn-why-bg hn-why-bg-left" />
                <img src={partner3} alt="" aria-hidden="true" className="hn-why-bg hn-why-bg-right" />
                
                <motion.h2
                    className="hn-why-label"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    ✦ why DT Wallet stands out ✦
                </motion.h2>

                <div className="hn-features">
                    {features.map((f, i) => (
                        <motion.div
                            key={f.title}
                            className={`hn-feature hn-feature-${f.side}`}
                            initial={{ opacity: 0, x: f.side === 'left' ? -80 : 80 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.8, ease: [0.22, 0.9, 0.3, 1] }}
                        >
                            <div className="hn-feature-visual">
                                <div className="hn-feature-blob"></div>
                                <img
                                    src={f.img}
                                    alt={f.title}
                                    className={f.isIcon ? 'hn-feature-icon' : 'hn-feature-img'}
                                    loading="lazy"
                                />
                                {f.extra && (
                                    <img src={f.extra} alt="" aria-hidden="true" className="hn-feature-extra" loading="lazy" />
                                )}
                                <img src={pinkStars} alt="" aria-hidden="true" className="hn-feature-sparkle" />
                            </div>
                            <div className="hn-feature-text">
                                <h3>{f.title}</h3>
                                <p>{f.body}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* ===================== FULL VIDEO SECTION ===================== */}
            <section className="hn-full-video-section">
                <video
                    className="hn-full-video"
                    autoPlay
                    loop
                    muted
                    playsInline
                    src={slideVideo}
                >
                    Your browser does not support the video tag.
                </video>
            </section>

            {/* ===================== YELLOW CTA BAND ===================== */}
            <section className="hn-cta-band">
                <svg className="hn-wave hn-wave-top" viewBox="0 0 1440 60" preserveAspectRatio="none">
                    <path d="M0,30 Q60,0 120,30 T240,30 T360,30 T480,30 T600,30 T720,30 T840,30 T960,30 T1080,30 T1200,30 T1320,30 T1440,30 V60 H0 Z" fill="#ffc107" />
                </svg>

                <motion.div
                    className="hn-cta-band-inner"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                >
                    <h2>Ready to explore top brands, earn rewards & unlock exclusive deals?</h2>
                    <Link to="/dt-shop" className="hn-cta hn-cta-dark">
                        <span>Get Started with DT Wallet</span>
                        <span className="hn-cta-arrow">→</span>
                    </Link>
                </motion.div>
            </section>

            {/* ===================== PARTNERS ===================== */}
            <section className="hn-partners">
                {/* Background decorations */}
                <img src={partner1} alt="" aria-hidden="true" className="hn-partners-bg hn-partners-bg-left" />
                <img src={partner3} alt="" aria-hidden="true" className="hn-partners-bg hn-partners-bg-right" />
                
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                >
                    <div className="hn-partners-head">
                        <h2>our <span>Partners</span></h2>
                    </div>
                </motion.div>

                <Marquee gradient={false} speed={45} pauseOnHover className="hn-marquee">
                    {partnerBrands.map((b) => (
                        <div key={b.id} className="hn-partner-chip">
                            <div className="hn-partner-logo">
                                {b.logo ? (
                                    <img src={b.logo} alt={b.brand} />
                                ) : (
                                    <span style={{ background: `linear-gradient(135deg, ${b.gradient[0]}, ${b.gradient[1]})` }}>
                                        {b.initial}
                                    </span>
                                )}
                            </div>
                            <span className="hn-partner-name">{b.brand}</span>
                        </div>
                    ))}
                </Marquee>

                <Marquee gradient={false} speed={35} direction="right" className="hn-marquee">
                    {partnerBrands.slice().reverse().map((b) => (
                        <div key={`r-${b.id}`} className="hn-partner-chip">
                            <div className="hn-partner-logo">
                                {b.logo ? (
                                    <img src={b.logo} alt={b.brand} />
                                ) : (
                                    <span style={{ background: `linear-gradient(135deg, ${b.gradient[0]}, ${b.gradient[1]})` }}>
                                        {b.initial}
                                    </span>
                                )}
                            </div>
                            <span className="hn-partner-name">{b.brand}</span>
                        </div>
                    ))}
                </Marquee>
            </section>

            {/* ===================== BIG WORDMARK ===================== */}
            <section className="hn-wordmark">
                <motion.h2
                    className="hn-wordmark-text"
                    initial={{ opacity: 0, scale: 0.85 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, amount: 0.5 }}
                    transition={{ duration: 1, ease: [0.22, 0.9, 0.3, 1] }}
                >
                    <img src={dtLogo} alt="DT Wallet Logo" className="hn-wordmark-logo" />
                </motion.h2>
                <motion.p
                    className="hn-wordmark-sub"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                >
                    Explore our DT Shop
                </motion.p>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                >
                    <Link to="/dt-shop" className="hn-cta hn-cta-dark">
                        <img src={illustrationBtn} alt="" aria-hidden="true" style={{ height: 22 }} />
                        <span>Visit DT Shop</span>
                    </Link>
                </motion.div>
            </section>
        </div>
    );
}

export default HomeNew;
